import { useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import PasswordField from '../components/ui/PasswordField';
import { emailValidationMessage, normalizeEmail, isValidEmail } from '../utils/emailValidation';
import { isBrowseIntent, safeNextPath } from '../utils/authRedirect';
import { LegalFooterLinks } from '../components/legal/LegalDocument';
import RecaptchaWidget from '../components/auth/RecaptchaWidget';

const ROLES = [
  { value: 'investor',      label: 'I want to evaluate properties',     sublabel: 'Investor' },
  { value: 'auction_house', label: 'I represent an auction house',      sublabel: 'Auction House' },
  { value: 'estate_agent',  label: 'I represent an estate agent',       sublabel: 'Estate Agent' },
  { value: 'valuer',        label: 'I provide professional valuations', sublabel: 'Valuer' },
  { value: 'deal_source',   label: 'I source properties for investors', sublabel: 'Deal Source' },
];

export default function Register() {
  const [searchParams] = useSearchParams();
  const next = safeNextPath(searchParams.get('next'));
  const browseMode = isBrowseIntent(next);
  const loginHref = next !== '/dashboard' ? `/login?next=${encodeURIComponent(next)}` : '/login';

  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    role: 'investor',
    company_name: '',
  });
  const [loading, setLoading] = useState(false);
  const getCaptchaTokenRef = useRef(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  const isUploader = form.role === 'auction_house' || form.role === 'deal_source' || form.role === 'estate_agent';

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailMsg = emailValidationMessage(form.email);
    if (emailMsg) {
      toast.error(emailMsg);
      return;
    }
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (form.password !== form.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (!form.full_name.trim()) {
      toast.error('Full name is required');
      return;
    }

    setLoading(true);
    try {
      const email = normalizeEmail(form.email);
      const role = browseMode ? 'investor' : form.role;
      const result = await register({
        email,
        password: form.password,
        full_name: form.full_name.trim(),
        role,
        company_name: isUploader && !browseMode ? form.company_name : undefined,
        recaptcha_token: getCaptchaTokenRef.current?.() || undefined,
      });
      toast.success(result.message || 'Check your email to verify your account');
      navigate(`/verify-email?email=${encodeURIComponent(email)}`, { replace: true });
    } catch (err) {
      toast.error(err?.message || err?.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className={`w-full ${browseMode ? 'max-w-md' : 'max-w-md md:max-w-3xl'} bg-slate-900 border border-slate-800 rounded-2xl p-8`}>
        <Link to="/" className="block">
          <img src="/propscope-logo.png" alt="PropScope"
            className="h-24 w-auto object-contain mx-auto mb-2 hover:opacity-90 transition-opacity" />
        </Link>
        <p className="text-slate-500 text-xs tracking-wide text-center mb-8">Analyse Smarter. Invest Better.</p>
        <h1 className="text-xl font-semibold text-white mb-2 text-center">
          {browseMode ? 'Create your free account' : 'Create your account'}
        </h1>
        <p className="text-slate-500 text-sm text-center mb-6">
          {browseMode
            ? 'Register with your email. We will send a verification link before you can sign in.'
            : 'Create your account with email and password. Verify your email from the link we send you.'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {browseMode ? (
            <>
              <RegisterFields form={form} set={set} />
              <RegisterFooter
                loading={loading}
                getCaptchaTokenRef={getCaptchaTokenRef}
              />
            </>
          ) : (
            <>
              <div className="md:grid md:grid-cols-2 md:gap-8 md:items-start">
                <div className="space-y-4">
                  <RegisterFields form={form} set={set} />
                  {isUploader && (
                    <div>
                      <label className="block text-sm text-slate-400 mb-1">Company name</label>
                      <input
                        type="text"
                        value={form.company_name}
                        onChange={set('company_name')}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                  )}
                </div>

                <div className="mt-4 md:mt-0">
                  <label className="block text-sm text-slate-400 mb-2">My role here is…?</label>
                  <div className="space-y-2">
                    {ROLES.map((r) => (
                      <label
                        key={r.value}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          form.role === r.value
                            ? 'border-emerald-500 bg-emerald-500/10'
                            : 'border-slate-700 hover:border-slate-600'
                        }`}
                      >
                        <input
                          type="radio"
                          name="role"
                          value={r.value}
                          checked={form.role === r.value}
                          onChange={set('role')}
                          className="accent-emerald-500 shrink-0"
                        />
                        <div className="min-w-0">
                          <div className="text-white text-sm">{r.label}</div>
                          <div className="text-slate-500 text-xs">{r.sublabel}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <RegisterFooter
                loading={loading}
                getCaptchaTokenRef={getCaptchaTokenRef}
              />
            </>
          )}
        </form>

        <p className="text-center text-slate-500 text-sm mt-4">
          Already have an account? <Link to={loginHref} className="text-emerald-400 hover:underline">Sign in</Link>
        </p>
        <p className="text-center text-slate-600 text-sm mt-3">
          <Link to="/" className="hover:text-slate-400">Homepage</Link>
        </p>
        <LegalFooterLinks className="mt-4" />
      </div>
    </div>
  );
}

function RegisterFields({ form, set }) {
  return (
    <>
      <div>
        <label className="block text-sm text-slate-400 mb-1">Full name</label>
        <input
          type="text"
          value={form.full_name}
          onChange={set('full_name')}
          required
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
        />
      </div>
      <div>
        <label htmlFor="register-email" className="block text-sm text-slate-400 mb-1">Email</label>
        <input
          id="register-email"
          type="email"
          value={form.email}
          onChange={set('email')}
          required
          autoComplete="username"
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
        />
        {form.email && !isValidEmail(form.email) && (
          <p className="text-amber-400 text-xs mt-1">Enter a valid email address</p>
        )}
      </div>
      <PasswordField
        id="register-password"
        value={form.password}
        onChange={set('password')}
        autoComplete="new-password"
        minLength={8}
        placeholder="At least 8 characters"
      />
      <PasswordField
        id="register-confirm"
        label="Confirm password"
        value={form.confirmPassword}
        onChange={set('confirmPassword')}
        autoComplete="new-password"
        minLength={8}
      />
    </>
  );
}

function RegisterFooter({ loading, getCaptchaTokenRef }) {
  return (
    <div className="space-y-4 pt-4 md:pt-6 border-t border-slate-800/80">
      <RecaptchaWidget
        onReady={(getter) => { getCaptchaTokenRef.current = getter; }}
        disabled={loading}
      />
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg py-2.5 font-medium transition-colors disabled:opacity-50"
      >
        {loading ? 'Creating account...' : 'Create account'}
      </button>
      <p className="text-slate-500 text-xs text-center leading-relaxed">
        By creating an account you agree to our{' '}
        <Link to="/terms" className="text-emerald-400 hover:underline">Terms</Link>
        {' '}and{' '}
        <Link to="/privacy" className="text-emerald-400 hover:underline">Privacy Policy</Link>.
      </p>
    </div>
  );
}
